import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { Dropdown, DropdownProps, Label } from 'semantic-ui-react';
import CompanyRepo from '../../../../utils/repository/companyRepo';
import CompanyUtils from '../../../../utils/companyUtils';
import Utils from '../../../../utils/descriptor/descriptorUtils';
import { CompanyWithSchemas } from '../../types';
import { CompanyInfoType } from '../../../../schemas/company.schemas';
import { useComparisonCompanies, useComparisonCompanyActions } from '../../context';
import './companySelector.css';
import { CompanyInfoListResponseSchemaType } from '../../../../utils/repository/companyRepo/schemas';
import { RESPONSE_STATUS } from '../../../../constnats/general';

const SEARCH_DEBOUNCE_TIME = 300;

function CompanySelector() {
    const [availableCompanies, setAvailableCompanies] = useState<CompanyInfoType[]>([]);
    const [loading, setLoading] = useState(true);
    const companies = useComparisonCompanies();
    const { addCompany, removeCompany } = useComparisonCompanyActions();
    const [searchParams] = useSearchParams();

    const fetchInitialPage = useCallback(() => {
        setLoading(true);
        CompanyRepo.list(0, (response: CompanyInfoListResponseSchemaType) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                setAvailableCompanies(response.data.content);
                setLoading(false);
            }
        });
    }, []);

    useEffect(() => {
        fetchInitialPage();
    }, [fetchInitialPage]);

    const selectedIds = companies.map((c) => c.id);

    const options = useMemo(() => {
        const selectedOptions = companies
            .filter((c) => !availableCompanies.some((ac) => ac.id === c.id))
            .map((c) => ({
                key: c.id,
                value: c.id,
                text: CompanyUtils.getCompanyTitle(c),
            }));

        const availableOptions = availableCompanies.map((c) => ({
            key: c.id,
            value: c.id,
            text: CompanyUtils.getCompanyTitle(c),
        }));

        return [...selectedOptions, ...availableOptions];
    }, [availableCompanies, companies]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = (_e: any, { value }: any) => {
        const ids = value as number[];
        const currentIds = new Set(selectedIds);
        const newIds = new Set(ids);

        for (const id of ids) {
            if (!currentIds.has(id)) {
                loadCompanyDetail(id);
            }
        }

        for (const id of selectedIds) {
            if (!newIds.has(id)) {
                removeCompany(id);
            }
        }
    };

    const debouncedSearch = useCallback(
        debounce((query: string) => {
            if (!query) {
                fetchInitialPage();
                return;
            }
            setLoading(true);
            CompanyRepo.search(query, (results: CompanyInfoType[]) => {
                setAvailableCompanies(results);
                setLoading(false);
            });
        }, SEARCH_DEBOUNCE_TIME),
        [fetchInitialPage],
    );

    const handleSearchChange = (_e: React.SyntheticEvent, { searchQuery }: DropdownProps) => {
        debouncedSearch(searchQuery as string);
    };

    const loadCompanyDetail = (companyId: number) => {
        CompanyRepo.get(companyId, (company: CompanyWithSchemas) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            Utils.addFullCategoriesToSchemas(company.schemas as any, () => {
                addCompany(company);
            });
        });
    };

    useEffect(() => {
        const param = searchParams.get('companyIds');
        if (!param) {
            return;
        }
        const ids = param.split(',').map(Number).filter(Boolean);
        for (const companyId of ids) {
            loadCompanyDetail(companyId);
        }
    }, []);

    return (
        <div className="company-selector">
            <label>Companies</label>
            <Dropdown
                placeholder="Select companies to compare..."
                fluid
                multiple
                search={(opts) => opts}
                selection
                loading={loading}
                options={options}
                value={selectedIds}
                onChange={handleChange}
                onSearchChange={handleSearchChange}
                renderLabel={(item) => <Label content={item.text} removeIcon="delete" />}
            />
        </div>
    );
}

export default CompanySelector;
